"use client";
import { useState, useEffect, useRef } from "react";
import { z } from "zod";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogClose,
  DialogFooter,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { Form, FormField } from "@/components/ui/form";
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "../../@/components/ui/field";
import { Textarea } from "../../@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../@/components/ui/select";
import {
  Plus,
  X,
  Edit2,
  Trash2,
  GripVertical,
  Save,
  Loader2,
  Info,
} from "lucide-react";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import MarkdownEditorDialog from "../components/MarkdownEditorDialog";
import { toast } from "sonner";
import DeleteQuestion from "./deleteQuestion";
import { Spinner } from "../components/spinner";
import QuestionFormCard from "../components/QuestionFormCard";
import { cn } from "@/lib/utils";

const questionSchema = z
  .object({
    id: z.union([z.string(), z.number()]).optional(),
    questionId: z.union([z.string(), z.number()]).optional(),
    type: z.enum(["multiple_choice", "true_false", "numeric"]),
    question_text: z.string().min(1, "Question is required"),
    score: z.number().min(1, "Score is required"),
    order: z.number().min(1, "Order is required"),
    question_content: z.string().optional(),
    explanation: z.string().optional(),
    answers: z
      .array(
        z.object({
          id: z.number().optional(),
          question_id: z.number().optional(),
          text: z.string().min(1, "Answer is required"),
          is_correct: z.boolean(),
        }),
      )
      .optional()
      .default([]),
    answer_text: z.string().optional(),
    isExisting: z.boolean().optional().default(true),
    isModified: z.boolean().optional().default(false),
    isDeleted: z.boolean().optional().default(false),
  })
  .refine(
    (data) => {
      if (data.type === "multiple_choice") {
        const validAnswers = (data.answers || []).filter(
          (a) => a.text && a.text.trim() !== "",
        );
        return (
          validAnswers.length >= 2 && validAnswers.some((a) => a.is_correct)
        );
      }
      if (data.type === "numeric") {
        const validAnswers = (data.answers || []).filter(
          (answer) => answer.text && answer.text.trim() !== "",
        );
        return (
          validAnswers.length >= 1 && validAnswers.some((a) => a.is_correct)
        );
      }
      if (data.type === "true_false") {
        const validAnswers = (data.answers || []).filter(
          (answer) => answer.text && answer.text.trim() !== "",
        );
        const texts = validAnswers.map((a) => a.text.toLowerCase());
        return (
          validAnswers.length === 2 &&
          texts.includes("true") &&
          texts.includes("false") &&
          validAnswers.some((a) => a.is_correct)
        );
      }
      return true;
    },
    {
      message: "Invalid answers for this question type",
      path: ["answers"],
    },
  );

const editQuizSchema = z.object({
  id: z.union([z.string(), z.number()]),
  title: z.string().min(1, "Quiz title is required"),
  description: z.string().optional(),
  time_limit: z.coerce.number().min(0, "Time limit must be at least 0 minutes"),
  maximum_attempts: z.coerce
    .number()
    .min(0, "Maximum attempts must be at least 0"),
  passmark: z.coerce
    .number()
    .min(0)
    .max(100, "Passmark must be between 0 and 100"),
  questions: z.array(questionSchema).optional(),
});

export function UnifiedEditQuizDialog({
  quiz,
  onQuizUpdated,
  children,
  isLoadingQuestions = false,
  refetchTopics,
  courseTitle = "default-course",
  courseSlug = "default-slug",
}) {
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1 for quiz details, 2 for questions
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const formContainerRef = useRef(null);

  // Markdown editor states
  const [markdownEditorOpen, setMarkdownEditorOpen] = useState(false);
  const [questionContentEditorOpen, setQuestionContentEditorOpen] =
    useState(false);
  const [explanationEditorOpen, setExplanationEditorOpen] = useState(false);

  const { getToken } = useAuth();

  const form = useForm({
    resolver: zodResolver(editQuizSchema),
    defaultValues: {
      id: quiz?.id || "",
      title: quiz?.title || "",
      description: quiz?.description || "",
      time_limit: quiz?.time_limit || 0,
      maximum_attempts: quiz?.maximum_attempts || 0,
      passmark: quiz?.passmark || 0,
      questions: [],
    },
  });

  const {
    fields: questions,
    append,
    remove,
    update,
    replace,
  } = useFieldArray({
    control: form.control,
    name: "questions",
  });

  // Question form for adding/editing questions
  const questionForm = useForm({
    resolver: zodResolver(questionSchema),
    defaultValues: {
      type: "multiple_choice",
      question_text: "",
      score: 1,
      order: 1,
      question_content: "",
      explanation: "",
      answers: [
        { text: "", is_correct: false },
        { text: "", is_correct: false },
      ],
      answer_text: "",
      isExisting: false,
      isModified: false,
    },
  });

  const {
    fields: answers,
    append: appendAnswer,
    remove: removeAnswer,
    replace: replaceAnswers,
  } = useFieldArray({
    control: questionForm.control,
    name: "answers",
  });

  const questionType = questionForm.watch("type");
  const [correctAnswerIndex, setCorrectAnswerIndex] = useState(null);

  // Markdown editor handlers for quiz description
  const handleMarkdownEditorClose = () => {
    setMarkdownEditorOpen(false);
  };

  const handleMarkdownSave = (value) => {
    form.setValue("description", value);
    setMarkdownEditorOpen(false);
  };

  const handleOpenMarkdownEditor = () => {
    setMarkdownEditorOpen(true);
  };

  // Handlers for Question Content Markdown Editor
  const handleQuestionContentEditorClose = () => {
    setQuestionContentEditorOpen(false);
  };

  const handleQuestionContentSave = (value) => {
    questionForm.setValue("question_content", value);
    setQuestionContentEditorOpen(false);
  };

  const handleOpenQuestionContentEditor = () => {
    setQuestionContentEditorOpen(true);
  };

  // Handlers for Explanation Markdown Editor
  const handleExplanationEditorClose = () => {
    setExplanationEditorOpen(false);
  };

  const handleExplanationSave = (value) => {
    questionForm.setValue("explanation", value);
    setExplanationEditorOpen(false);
  };

  const handleOpenExplanationEditor = () => {
    setExplanationEditorOpen(true);
  };

  // Load quiz data when dialog opens
  useEffect(() => {
    if (open && quiz) {
      form.reset({
        id: quiz.id,
        title: quiz.title || "",
        description: quiz.description || "",
        time_limit: quiz.time_limit || 0,
        maximum_attempts: quiz.maximum_attempts || 0,
        passmark: quiz.passmark || 0,
        questions: quiz.questions || [],
      });

      // If questions are provided directly, use them
      if (quiz.questions && quiz.questions.length > 0) {
        const transformedQuestions = quiz.questions.map((q) => ({
          ...q,
          isExisting: true,
          isModified: false,
          isDeleted: false,
        }));
        replace(transformedQuestions);
      } else {
        fetchExistingQuestions(quiz.id).then(replace);
      }
    }
  }, [open, quiz]);

  const fetchExistingQuestions = async (quizId) => {
    try {
      setIsLoadingData(true);
      const token = await getToken();
      if (!token) return [];
      const requestConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await axios.get(
        api_endpoint + `/quiz/questions/${quizId}`,
        requestConfig,
      );
      console.log("Fetched questions:", response.data);
      if (
        response.data?.status === "success" &&
        Array.isArray(response.data.questions_and_answers)
      ) {
        const transformedQuestions = response.data.questions_and_answers.map(
          (item) => {
            const { question, answers } = item;
            let patchedAnswers = answers.map((a) => ({
              id: a.id,
              question_id: a.question_id,
              text: a.answer_text,
              is_correct: !!a.is_correct,
            }));
            // Patch for true/false questions
            if (question.type === "true_false") {
              const texts = patchedAnswers.map((a) => a.text?.toLowerCase());
              if (!texts.includes("true")) {
                patchedAnswers.push({ text: "True", is_correct: false });
              }
              if (!texts.includes("false")) {
                patchedAnswers.push({ text: "False", is_correct: false });
              }
              patchedAnswers = [
                patchedAnswers.find(
                  (a) => a.text?.toLowerCase() === "true",
                ) || { text: "True", is_correct: false },
                patchedAnswers.find(
                  (a) => a.text?.toLowerCase() === "false",
                ) || { text: "False", is_correct: false },
              ];
            }
            return {
              id: Number(question.id),
              questionId: Number(question.id),
              type: question.type,
              question_text: question.question_text,
              score: question.score || 1,
              order: question.order || 1,
              question_content: question.question_content || "",
              explanation: question.explanation || "",
              answers: patchedAnswers,
              answer_text:
                question.type === "description"
                  ? answers[0]?.answer_text || ""
                  : "",
              isExisting: true,
              isModified: false,
              isDeleted: false,
            };
          },
        );
        return transformedQuestions;
      } else {
        return [];
      }
    } catch (error) {
      console.error("Failed to fetch questions:", error);
      return [];
    } finally {
      setIsLoadingData(false);
    }
  };

  const refetchQuestions = async () => {
    const questions = await fetchExistingQuestions(quiz.id);
    replace(questions);
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      const isValid = await form.trigger([
        "title",
        "description",
        "time_limit",
        "maximum_attempts",
        "passmark",
      ]);
      if (isValid) {
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentStep(2);
          setIsTransitioning(false);
          if (formContainerRef.current) {
            formContainerRef.current.scrollTop = 0;
          }
        }, 150);
      }
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentStep(1);
        setIsTransitioning(false);
        if (formContainerRef.current) {
          formContainerRef.current.scrollTop = 0;
        }
      }, 150);
    }
  };

  const handleQuestionTypeChange = (newType) => {
    questionForm.setValue("type", newType);
    setCorrectAnswerIndex(null);
    const questionId = questionForm.getValues("questionId");

    if (newType === "numeric") {
      replaceAnswers([{ text: "", is_correct: true, question_id: questionId }]);
    } else if (newType === "true_false") {
      replaceAnswers([
        { text: "True", is_correct: false, question_id: questionId },
        { text: "False", is_correct: false, question_id: questionId },
      ]);
    } else if (newType === "multiple_choice") {
      if (answers.length < 2) {
        replaceAnswers([
          { text: "", is_correct: false, question_id: questionId },
          { text: "", is_correct: false, question_id: questionId },
        ]);
      }
    }
  };

  const handleCorrectAnswerChange = (index) => {
    setCorrectAnswerIndex(index);
    const currentAnswers = questionForm.getValues("answers").map((a, idx) => ({
      ...a, // Preserve id and question_id
      is_correct: idx === index,
    }));
    questionForm.setValue("answers", currentAnswers);
  };

  const addQuestion = () => {
    setEditingQuestion(null);
    questionForm.reset({
      type: "multiple_choice",
      question_text: "",
      score: 1,
      order: questions.length + 1,
      question_content: "",
      explanation: "",
      answers: [
        { text: "", is_correct: false },
        { text: "", is_correct: false },
      ],
      answer_text: "",
      isExisting: false,
      isModified: false,
    });
    setCorrectAnswerIndex(null);
    setShowQuestionForm(true);
  };

  const editQuestion = (index) => {
    const question = questions[index];
    setEditingQuestion(index);
    questionForm.reset({
      ...question,
      isModified: true,
    });
    setCorrectAnswerIndex(
      question.answers?.findIndex((a) => a.is_correct) ?? null,
    );
    setShowQuestionForm(true);
  };

  const saveQuestion = async (data) => {
    const questionData = {
      ...data,
      id:
        editingQuestion !== null
          ? questions[editingQuestion].id
          : Date.now().toString(),
      questionId:
        editingQuestion !== null
          ? questions[editingQuestion].questionId
          : undefined,
    };

    if (editingQuestion !== null) {
      // If the question is existing, update it on the backend
      if (
        questions[editingQuestion].isExisting &&
        questions[editingQuestion].questionId
      ) {
        const token = await getToken();
        const requestConfig = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };
        // Prepare the payload (match AddQuestionToQuizDialog)
        const updatePayload = {
          question_text: questionData.question_text,
          question_content: questionData.question_content || "",
          type: questionData.type,
          order: questionData.order,
          score: questionData.score,
          explanation: questionData.explanation || null,
          answers: questionData.answers,
          answer_text: questionData.answer_text || "",
        };
        await axios.put(
          api_endpoint +
            `/quiz/question/update/${questions[editingQuestion].questionId}`,
          updatePayload,
          requestConfig,
        );
      }
      // Update local state
      update(editingQuestion, {
        ...questionData,
        isModified: false, // After saving, it's no longer "modified"
      });
      toast.success("Question updated successfully");
    } else {
      // Add new question
      append({
        ...questionData,
        isExisting: false,
        isModified: false,
      });
      toast.success("Question created successfully");
    }

    setShowQuestionForm(false);
    setEditingQuestion(null);
    questionForm.reset();
    setCorrectAnswerIndex(null);
  };

  const deleteQuestion = (index) => {
    const question = questions[index];
    if (question.isExisting) {
      // Mark existing question for deletion instead of removing immediately
      update(index, {
        ...question,
        isDeleted: true,
      });
    } else {
      // Remove new questions immediately
      remove(index);
    }
    toast.success("Question deleted successfully");
  };

  const cancelQuestionEdit = () => {
    setShowQuestionForm(false);
    setEditingQuestion(null);
    questionForm.reset();
    setCorrectAnswerIndex(null);
  };

  const onSubmit = async (values) => {
    try {
      setIsSubmitting(true);

      const token = await getToken();
      if (!token) return;
      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      // 1. Update quiz details
      const quizPayload = {
        title: values.title,
        description: values.description || null,
        time_limit: values.time_limit,
        maximum_attempts: values.maximum_attempts,
        passmark: values.passmark,
      };
      await axios.put(
        api_endpoint + `/quiz/update/${values.id}`,
        quizPayload,
        reqConfig,
      );

      // 2. Process questions
      for (const q of values.questions || []) {
        if (q.isDeleted && q.questionId) {
          await axios.delete(
            api_endpoint + `/quiz/question/delete/${q.questionId}`,
            reqConfig,
          );
        } else if (!q.isExisting) {
          await axios.post(
            api_endpoint + "/quiz/question/create",
            {
              quiz_id: values.id,
              question_text: q.question_text,
              question_content: q.question_content || "",
              type: q.type,
              order: q.order,
              score: q.score,
              explanation: q.explanation || null,
              answers: q.answers,
              answer_text: q.answer_text || "",
            },
            reqConfig,
          );
        } else if (q.isExisting && q.questionId) {
          const answersPayload = q.answers.map((a) => ({
            // For new answers (like when changing question type), don't include id
            ...(a.id ? { id: a.id } : {}),
            text: a.text,
            is_correct: a.is_correct,
            question_id: q.questionId,
          }));

          await axios.put(
            api_endpoint + `/quiz/question/update/${q.questionId}`,
            {
              question_text: q.question_text,
              question_content: q.question_content || "",
              type: q.type,
              order: q.order,
              score: q.score,
              explanation: q.explanation || null,
              answers: answersPayload,
            },
            reqConfig,
          );
        }
      }

      if (onQuizUpdated) {
        onQuizUpdated({
          ...values,
          questions: values.questions?.filter((q) => !q.isDeleted) || [],
        });
      }
      if (refetchTopics) refetchTopics();
      toast.success("Quiz updated successfully");
      setOpen(false);
      setCurrentStep(1);
    } catch (error) {
      console.error("Failed to update quiz:", error);
      toast.error("Failed to update quiz. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
    setCurrentStep(1);
    setShowQuestionForm(false);
    setEditingQuestion(null);
    form.reset();
  };

  // Filter out deleted questions for display
  const visibleQuestions = questions.filter((q) => !q.isDeleted);
  const totalPoints = visibleQuestions.reduce(
    (sum, q) => sum + (q.score || 0),
    0,
  );

  return (
    <div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{children}</DialogTrigger>

        <DialogContent className="w-[95vw] max-h-[90vh] flex flex-col bg-(--surface-panel) text-(--neutral-900) border border-(--neutral-300) overflow-hidden">
          <div
            ref={formContainerRef}
            className={`flex-1 flex flex-col overflow-y-auto transition-all duration-150 ease-in-out ${isTransitioning ? "opacity-0 translate-x-2" : "opacity-100 translate-x-0"}`}
          >
            {/* Step 1: Quiz Details */}
            {currentStep === 1 && (
              <div
                id="edit-quiz-details-section"
                className="flex flex-col flex-1"
              >
                <DialogHeader className="border-(--neutral-300)">
                  <DialogTitle className="font-heading text-(--neutral-900)">
                    Edit Quiz
                  </DialogTitle>
                  <DialogDescription className="text-(--neutral-600)">
                    Update quiz details and manage questions
                  </DialogDescription>
                </DialogHeader>

                <div className="overflow-y-auto">
                  <Form {...form}>
                    <div className="space-y-6">
                      <h3 className="text-lg font-semibold text-(--neutral-900)">
                        Quiz Details
                      </h3>

                      <FormField
                        control={form.control}
                        name="title"
                        render={({ field, fieldState }) => (
                          <Field>
                            <FieldLabel>Quiz Title *</FieldLabel>
                            <FieldContent>
                              <Input
                                {...field}
                                placeholder="Intro to Machine Learning"
                                className={cn(
                                  "bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) focus-visible:ring-0 focus:border-[var(--color-accent)] mt-1",
                                  fieldState.error &&
                                    "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                                )}
                              />
                              <FieldError
                                errors={
                                  fieldState.error
                                    ? [{ message: "This is required" }]
                                    : []
                                }
                              />
                            </FieldContent>
                          </Field>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="description"
                        render={({ field, fieldState }) => (
                          <Field>
                            <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                              <FieldLabel>
                                Description
                                <div className="flex items-center gap-1 text-xs text-(--neutral-600) font-body">
                                  <Info className="w-3 h-3" />
                                  Optional
                                </div>
                              </FieldLabel>
                              {/* <Button
                                type="button"
                                onClick={handleOpenMarkdownEditor}
                                size="sm"
                                variant="outline"
                                className="btn-outline-sm rounded-[4px] hover:bg-blue hover:text-white hover:border-transparent font-body"
                              >
                                Edit in Fullscreen
                              </Button> */}
                            </div>
                            <FieldContent>
                              <Textarea
                                {...field}
                                rows={3}
                                className={cn(
                                  "min-h-[120px] resize-none border border-(--neutral-300) bg-(--surface-panel) text-(--neutral-900) placeholder:text-(--neutral-500) focus:border-[var(--color-accent)] focus:ring-0 font-body mt-1",
                                  fieldState.error &&
                                    "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                                )}
                                placeholder="Brief description of what the quiz covers"
                              />
                              <FieldError errors={[]} />
                            </FieldContent>
                          </Field>
                        )}
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="time_limit"
                          render={({ field, fieldState }) => (
                            <Field>
                              <FieldLabel>
                                Time Limit (Minutes) *
                                <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-body">
                                  (0 for unlimited)
                                </div>
                              </FieldLabel>
                              <FieldContent>
                                <Input
                                  {...field}
                                  type="number"
                                  min="0"
                                  className={cn(
                                    "bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) focus-visible:ring-0 focus:border-[var(--color-accent)] mt-1",
                                    fieldState.error &&
                                      "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                                  )}
                                />
                                <FieldError
                                  errors={
                                    fieldState.error
                                      ? [{ message: "This is required" }]
                                      : []
                                  }
                                />
                              </FieldContent>
                            </Field>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="maximum_attempts"
                          render={({ field, fieldState }) => (
                            <Field>
                              <FieldLabel>
                                Maximum Attempts *
                                <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-body">
                                  (0 for unlimited)
                                </div>
                              </FieldLabel>
                              <FieldContent>
                                <Input
                                  {...field}
                                  type="number"
                                  min="0"
                                  className={cn(
                                    "bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) focus-visible:ring-0 focus:border-[var(--color-accent)] mt-1",
                                    fieldState.error &&
                                      "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                                  )}
                                />
                                <FieldError
                                  errors={
                                    fieldState.error
                                      ? [{ message: "This is required" }]
                                      : []
                                  }
                                />
                              </FieldContent>
                            </Field>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="passmark"
                        render={({ field }) => {
                          const value = field.value || 0;
                          const fillPercent = Math.min(Math.max(value, 0), 100);

                          return (
                            <Field>
                              <FieldLabel>Passing Score *</FieldLabel>
                              <FieldContent>
                                <div className="flex items-center gap-3 mt-1">
                                  <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={value}
                                    onChange={(e) => {
                                      let newValue = Number(e.target.value);
                                      if (newValue > 100) newValue = 100;
                                      field.onChange(newValue);
                                    }}
                                    className={cn(
                                      "w-16 border border-(--neutral-300) rounded px-2 py-1 text-sm text-center bg-(--surface-panel) text-(--neutral-900)",
                                      form.formState.errors?.passmark &&
                                        "border-(--color-destructive)",
                                    )}
                                  />
                                  <div className="flex-1">
                                    <input
                                      type="range"
                                      min={0}
                                      max={100}
                                      value={value}
                                      onChange={(e) =>
                                        field.onChange(Number(e.target.value))
                                      }
                                      className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                                      style={{
                                        background: `linear-gradient(to right, var(--color-destructive) 0%, var(--color-destructive) ${fillPercent}%, var(--neutral-200) ${fillPercent}%, var(--neutral-200) 100%)`,
                                      }}
                                    />
                                  </div>
                                  <span className="text-sm text-(--neutral-600)">
                                    {value}%
                                  </span>
                                </div>
                                <FieldError
                                  errors={
                                    form.formState.errors?.passmark
                                      ? [{ message: "This is required" }]
                                      : []
                                  }
                                />
                              </FieldContent>
                            </Field>
                          );
                        }}
                      />
                    </div>
                  </Form>
                </div>

                <DialogFooter className="mt-4">
                  <DialogClose asChild>
                    <Button variant="outline" className="">
                      Cancel
                    </Button>
                  </DialogClose>
                  <Button onClick={handleNext} variant="" className="">
                    Edit Questions
                  </Button>
                </DialogFooter>
              </div>
            )}

            {/* Step 2: Edit Questions */}
            {currentStep === 2 && (
              <div
                id="edit-quiz-questions-section"
                className="flex flex-col flex-1"
              >
                <DialogHeader className="px-2 pt-6 order-[var(--neutral-300)]">
                  <DialogTitle className="font-heading text-(--neutral-900)">
                    Edit Questions
                  </DialogTitle>
                  <DialogDescription className="text-(--neutral-600)">
                    {visibleQuestions.length} questions • {totalPoints} points
                  </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-2 py-2 h-auto">
                  {isLoadingData ? (
                    <div className="flex items-center justify-center py-8">
                      <Spinner />
                    </div>
                  ) : (
                    <>
                      {/* Question List */}
                      <div className="space-y-4 mb-6">
                        {visibleQuestions.map((question, index) => (
                          <EditableQuestionCard
                            key={question.id || index}
                            question={question}
                            index={index}
                            onEdit={() => editQuestion(index)}
                            onDelete={() => deleteQuestion(index)}
                            refetchQuestions={refetchQuestions}
                          />
                        ))}

                        {visibleQuestions.length === 0 && !showQuestionForm && (
                          <div className="text-center py-8 text-(--neutral-600) border-2 border-(--neutral-300) rounded-lg bg-(--surface-base)">
                            No questions found. Click "Add Question" to get
                            started.
                          </div>
                        )}
                      </div>

                      {/* Add Question Button */}
                      {!showQuestionForm && (
                        <Button
                          onClick={addQuestion}
                          className="w-full"
                          variant="outline"
                        >
                          Add Question
                        </Button>
                      )}

                      {/* Question Form */}
                      <Dialog
                        open={showQuestionForm}
                        onOpenChange={setShowQuestionForm}
                      >
                        <QuestionFormCard
                          questionForm={questionForm}
                          answers={answers}
                          appendAnswer={appendAnswer}
                          removeAnswer={removeAnswer}
                          questionType={questionType}
                          correctAnswerIndex={correctAnswerIndex}
                          onTypeChange={handleQuestionTypeChange}
                          onCorrectAnswerChange={handleCorrectAnswerChange}
                          onSave={questionForm.handleSubmit(saveQuestion)}
                          onCancel={cancelQuestionEdit}
                          isEditing={editingQuestion !== null}
                          onOpenQuestionContentEditor={
                            handleOpenQuestionContentEditor
                          }
                          onOpenExplanationEditor={handleOpenExplanationEditor}
                          editCtaLabel="Save Changes"
                          dialogContentClassName="max-w-2xl w-[95vw] max-h-[90vh] flex flex-col p-0 bg-(--surface-panel) text-(--neutral-900) border border-(--neutral-300)"
                        />
                      </Dialog>
                    </>
                  )}
                </div>

                <div className="flex justify-between ">
                  <Button onClick={handleBack} variant="outline" className="">
                    Back
                  </Button>
                  <div className="flex gap-2">
                    <DialogClose asChild>
                      <Button
                        variant="outline"
                        className=""
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                    </DialogClose>
                    <Button
                      onClick={form.handleSubmit(onSubmit)}
                      disabled={isSubmitting}
                      className=""
                      variant=""
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        </>
                      ) : (
                        "Save Changes"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <MarkdownEditorDialog
        open={markdownEditorOpen}
        initialValue={form.watch("description") || ""}
        onClose={handleMarkdownEditorClose}
        onSave={handleMarkdownSave}
        contentFolder={courseSlug}
        contentName={courseTitle}
      />
      <MarkdownEditorDialog
        open={questionContentEditorOpen}
        initialValue={questionForm.watch("question_content") || ""}
        onClose={handleQuestionContentEditorClose}
        onSave={handleQuestionContentSave}
        contentFolder={courseSlug}
        contentName={courseTitle}
      />
      <MarkdownEditorDialog
        open={explanationEditorOpen}
        initialValue={questionForm.watch("explanation") || ""}
        onClose={handleExplanationEditorClose}
        onSave={handleExplanationSave}
        contentFolder={courseSlug}
        contentName={courseTitle}
      />
    </div>
  );
}

// Editable Question Card Component
function EditableQuestionCard({
  question,
  index,
  onEdit,
  onDelete,
  refetchQuestions,
}) {
  // const getStatusBadge = () => {
  //     if (!question.isExisting) {
  //         return <span className="text-xs font-medium text-green-600 uppercase bg-green-100 px-2 py-1 rounded-full">NEW</span>
  //     }
  //     if (question.isModified) {
  //         return <span className="text-xs font-medium text-yellow-600 uppercase bg-yellow-100 px-2 py-1 rounded-full">MODIFIED</span>
  //     }
  //     return null
  // }

  return (
    <div className="border border-(--neutral-300) rounded-lg p-4 bg-(--surface-panel)">
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <GripVertical className="w-4 h-4 text-(--neutral-500)" />
          <span className="text-xs font-medium text-[var(--neutral-50)] uppercase bg-[var(--color-accent)] px-2 py-1 rounded-full">
            {question.type?.replace("_", " ") || "MULTIPLE CHOICE"}
          </span>
          <span className="text-xs font-medium text-[var(--color-accent)] uppercase border border-[var(--color-accent)] px-2 py-1 rounded-full">
            {question.score} POINTS
          </span>
          {/* {getStatusBadge()} */}
        </div>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            className="text-(--neutral-500) hover:text-(--color-accent)"
          >
            <Edit2 className="w-4 h-4" />
          </Button>
          {question.isExisting && question.questionId ? (
            <DeleteQuestion
              questionId={question.questionId}
              questionText={question.question_text}
              onDeleted={async () => {
                await refetchQuestions(); // <-- refetch after delete
              }}
              triggerButtonProps={{
                className:
                  "text-(--neutral-500) hover:text-(--color-accent)",
                size: "icon",
              }}
            />
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(index)}
              type="button"
              className="text-(--neutral-500) hover:text-(--color-primary)"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <h6 className="font-medium mb-2 text-(--neutral-900)">
        {question.question_text}
      </h6>

      {/* {question.question_content && (
                <div className="bg-white border rounded-md p-3 text-sm text-gray-700 mb-3">
                    {question.question_content}
                </div>
            )} */}

      {question.type !== "description" && question.answers && (
        <div className="space-y-2">
          {question.answers.map((answer, answerIndex) => (
            <div key={answerIndex} className="flex items-center gap-2">
              <div
                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                  answer.is_correct
                    ? "bg-[var(--color-destructive)] border-(--color-destructive)"
                    : "border-(--neutral-300)"
                }`}
              >
                {answer.is_correct && (
                  <div className="w-2 h-2 bg-(--neutral-50) rounded-full" />
                )}
              </div>
              <span className="text-sm text-(--neutral-700)">
                {answer.text}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
